// @ts-nocheck
/**
 * Sales Orders Redux Sagas
 * Side effects for user-triggered actions
 */

import { call, put, select, takeLatest, takeLeading } from 'redux-saga/effects'
import type { PayloadAction } from '@reduxjs/toolkit'
import { extractErrorMessage } from '@ufc/core'
import { salesOrdersApi } from '../infrastructure/api/sales-orders-api'
import { salesOrdersActions } from './sales-orders.slice'
import type {
  SalesOrder,
  SalesOrderStats,
  SalesOrdersFilter,
  CreateSalesOrderRequest,
  PaginatedSalesOrders,
} from '../domain/types'

function* fetchSalesOrdersSaga(action: PayloadAction<SalesOrdersFilter | undefined>) {
  try {
    const result: PaginatedSalesOrders = yield call(salesOrdersApi.getSalesOrdersPaginated, action.payload)
    yield put(salesOrdersActions.fetchSalesOrdersSuccess(result))
  } catch (error) {
    yield put(salesOrdersActions.fetchSalesOrdersFailure(extractErrorMessage(error)))
  }
}

function* fetchSalesOrderStatsSaga() {
  try {
    // Use current filter so stats respect plant (factory) selection.
    const filter: SalesOrdersFilter = yield select((state: any) => state.salesOrders?.filter)
    const stats: SalesOrderStats = yield call(salesOrdersApi.getSalesOrderStats, filter)
    yield put(salesOrdersActions.fetchSalesOrderStatsSuccess(stats))
  } catch (error) {
    yield put(salesOrdersActions.fetchSalesOrderStatsFailure(extractErrorMessage(error)))
  }
}

function* fetchSkuOptionsSaga() {
  try {
    const result = yield call(salesOrdersApi.getSkuFilterOptions)
    yield put(salesOrdersActions.fetchSkuOptionsSuccess(result))
  } catch (error) {
    yield put(salesOrdersActions.fetchSkuOptionsFailure(extractErrorMessage(error)))
  }
}

function* createSalesOrderSaga(action: PayloadAction<CreateSalesOrderRequest>) {
  try {
    const salesOrder: SalesOrder = yield call(salesOrdersApi.createSalesOrder, action.payload)
    yield put(salesOrdersActions.createSalesOrderSuccess(salesOrder))
  } catch (error) {
    yield put(salesOrdersActions.createSalesOrderFailure(extractErrorMessage(error)))
  }
}

function* renewSalesOrderSaga(action: PayloadAction<{ salesOrderId: string; newEndDate: string }>) {
  try {
    const salesOrder: SalesOrder = yield call(
      salesOrdersApi.renewSalesOrder,
      action.payload.salesOrderId,
      action.payload.newEndDate
    )
    yield put(salesOrdersActions.renewSalesOrderSuccess(salesOrder))
  } catch (error) {
    yield put(salesOrdersActions.renewSalesOrderFailure(extractErrorMessage(error)))
  }
}

type UpdateSalesOrderPayload = { id: string } & Partial<CreateSalesOrderRequest>

function* updateSalesOrderSaga(action: PayloadAction<UpdateSalesOrderPayload>) {
  try {
    const { id, ...updates } = action.payload
    const salesOrder: SalesOrder = yield call(salesOrdersApi.updateSalesOrder, id, updates)
    yield put(salesOrdersActions.updateSalesOrderSuccess(salesOrder))
  } catch (error) {
    yield put(salesOrdersActions.updateSalesOrderFailure(extractErrorMessage(error)))
  }
}

function* deleteSalesOrderSaga(action: PayloadAction<string>) {
  try {
    yield call(salesOrdersApi.deleteSalesOrder, action.payload)
    yield put(salesOrdersActions.deleteSalesOrderSuccess(action.payload))
  } catch (error) {
    yield put(salesOrdersActions.deleteSalesOrderFailure(extractErrorMessage(error)))
  }
}

function* cancelSalesOrderSaga(action: PayloadAction<{ salesOrderId: string }>) {
  try {
    const salesOrder: SalesOrder = yield call(salesOrdersApi.cancelSalesOrder, action.payload.salesOrderId)
    yield put(salesOrdersActions.cancelSalesOrderSuccess(salesOrder))
  } catch (error) {
    yield put(salesOrdersActions.cancelSalesOrderFailure(extractErrorMessage(error)))
  }
}

function* duplicateSalesOrderSaga(action: PayloadAction<string>) {
  try {
    const salesOrder: SalesOrder = yield call(salesOrdersApi.duplicateSalesOrder, action.payload)
    yield put(salesOrdersActions.duplicateSalesOrderSuccess(salesOrder))
  } catch (error) {
    yield put(salesOrdersActions.duplicateSalesOrderFailure(extractErrorMessage(error)))
  }
}

/**
 * When the global header selection changes, update the sales orders factoryIds filter
 * and re-fetch the list.
 */
function* handleLocationsContextChangeSaga(action: PayloadAction<any>) {
  try {
    const locationsState: any = yield select((state: any) => state.locations)
    const derivedFactoryId = locationsState?.derivedFactoryId
    const selectedLocationId = locationsState?.currentLocationId

    if (derivedFactoryId) {
      yield put(salesOrdersActions.filterByFactory([derivedFactoryId]))
      return
    }

    if (selectedLocationId) {
      yield put(salesOrdersActions.filterByFactory([]))
      return
    }

    const factoriesState: any = yield select((state: any) => state.factories)
    const factory = factoriesState?.currentFactory ?? factoriesState?.selectedFactory
    const factoryIds = factory?.documentId ? [factory.documentId] : []
    yield put(salesOrdersActions.filterByFactory(factoryIds))
  } catch {
    // factories state may not exist
  }
}

export function* salesOrdersSaga() {
  yield takeLatest(salesOrdersActions.fetchSalesOrders.type, fetchSalesOrdersSaga)
  // takeLeading: ignore subsequent dispatches while the first is in-flight (prevents StrictMode double-mount duplicates)
  yield takeLeading(salesOrdersActions.fetchSalesOrderStats.type, fetchSalesOrderStatsSaga)
  yield takeLeading(salesOrdersActions.fetchSkuOptions.type, fetchSkuOptionsSaga)
  yield takeLatest(salesOrdersActions.createSalesOrder.type, createSalesOrderSaga)
  yield takeLatest(salesOrdersActions.renewSalesOrder.type, renewSalesOrderSaga)
  yield takeLatest(salesOrdersActions.updateSalesOrder.type, updateSalesOrderSaga)
  yield takeLatest(salesOrdersActions.deleteSalesOrder.type, deleteSalesOrderSaga)
  yield takeLatest(salesOrdersActions.cancelSalesOrder.type, cancelSalesOrderSaga)
  yield takeLatest(salesOrdersActions.duplicateSalesOrder.type, duplicateSalesOrderSaga)
  // Re-fetch sales orders when global header selection changes
  yield takeLatest('factories/selectFactory', handleLocationsContextChangeSaga)
  yield takeLatest('factories/clearFactorySelection', handleLocationsContextChangeSaga)
  yield takeLatest('locations/selectLocation', handleLocationsContextChangeSaga)
  yield takeLatest('locations/clearLocationSelection', handleLocationsContextChangeSaga)
}

// Legacy alias for backwards compatibility
export const contractsSaga = salesOrdersSaga
